// Boot module prefetch gate (perf/shell-boot, 2026-10-09).
//
// The composite /core/boot answer carries the tab's FULL workspace record —
// settings.workspaceModule included — long before the shell has mounted and
// the workspace turned authoritative (measured live on staging
// /w/vault/default: boot answered at ~4.0 s, the module's first read went out
// at 9.6 s). bootShell hands every boot row to `primeWorkspaceCache`; the hook
// below keeps that contract and, for the ONE record this boot resolves for the
// tab, starts `prefetchWorkspaceModule` (workspaceProjects.js) — fetch only,
// nothing injected. loadWorkspaceProject later joins the same flight.
//
// Imported by index.js only (like convergeBootWorkspace.js) — never through
// the functions barrel, so nothing can `el.call` it.

// Can this record be proven to be the workspace this boot resolves for the
// tab? Mirrors the first rungs of shared resolveActiveWorkspace: the pointer
// the /core/boot request carried, and the URL scope, which wins over the
// pointer. Anything it cannot prove is a "no" — the regular takeover pass then
// loads the module exactly as before.
//   wsId      the id the record is primed under
//   record    the full workspace record
//   pointer   the active-workspace pointer (what sdk.boot was asked for)
//   segments  { orgSlug, seg2 } — shared getWorkspaceRawSegments(pathname)
//   orgs      [{ id|_id, slug }] — the session's orgs, to resolve the URL org
export const bootModulePrefetchAllowed = ({ wsId, record, pointer, segments, orgs } = {}) => {
  if (!wsId || !record || !pointer) return false
  if (String(wsId) !== String(pointer)) return false
  const orgSlug = segments && segments.orgSlug
  const seg2 = segments && segments.seg2
  // No `/w/<org>` scope: the pointer IS this boot's workspace.
  if (!orgSlug) return true
  // `/w/<org>` alone resolves HQ-or-pointer from lists the hook does not hold.
  if (!seg2) return false
  // Review D4: a handle is not unique across orgs (each org may have a
  // "default"), so the URL's ORG must be this record's org first.
  const idOf = (v) => (v && typeof v === 'object' ? v._id || v.id : v)
  const org = Array.isArray(orgs)
    ? orgs.find((o) => o && String(o.slug || '').toLowerCase() === String(orgSlug).toLowerCase())
    : null
  const recOrg = idOf(record.organization || record.organizationId || record.org_id || record.org)
  if (!org || !idOf(org) || !recOrg || String(idOf(org)) !== String(recOrg)) return false
  // `/w/<org>/<handle>`: the handle must name this record (the same
  // slug → key → id precedence the URL canonicalizer writes). A route
  // segment on the org's HQ also lands here and is a "no".
  const want = String(seg2).toLowerCase()
  return [record.slug, record.key, record._id, record.id].some(
    (v) => v !== undefined && v !== null && v !== '' && String(v).toLowerCase() === want
  )
}

// The `primeWorkspaceCache` index.js hands to bootShell: prime first (same
// arguments, same return — bootShell's contract is untouched), then start the
// module prefetch for the tab's workspace. Never throws into the boot.
//   prime  the real cache primer (getWorkspaceCached.js primeWorkspaceCache)
//   deps   { getPointer, getSegments, getOrgs, getSdk, prefetch }
export const primeAndPrefetchModule = (prime, deps) => (wsId, record) => {
  const primed = prime(wsId, record)
  if (!primed) return primed
  try {
    const allowed = bootModulePrefetchAllowed({
      wsId,
      record,
      pointer: deps.getPointer(),
      segments: deps.getSegments(),
      orgs: deps.getOrgs ? deps.getOrgs() : null
    })
    if (allowed) deps.prefetch(deps.getSdk(), wsId, record)
  } catch (err) {
    console.warn('[workspace] boot module prefetch skipped', err && err.message)
  }
  return primed
}
